"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useState } from "react";
import { useTracking } from "react-tracking";
import { Text } from "../../DataDisplay/Text/index.js";
import { INVALID_PROP_ERROR, COMPONENT_NAME } from "./constants.js";
import { combineSearchParams } from "./lib.js";
import { CustomStylesLink } from "./styles.js";
import { Modal } from "../../Surfaces/Modal/index.js";
import { getDataTestId } from "../../Util/index.js";
const Link = forwardRef(
  ({
    children,
    variant = "inherit",
    underline = "hover",
    href = "#",
    hasExternalLinkWarning = false,
    warningCloseAriaLabel,
    warningText,
    warningTitle,
    warningPrimaryButtonText,
    warningSecondaryButtonText,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    color = "inherit",
    target = "_self",
    sx,
    dataPrivate = false,
    dataBiPrivate = false,
    uniqueId,
    onClick,
    onKeyDown,
    onMouseEnter,
    preserveSearchParams = false,
    ...rest
  }, ref) => {
    const { trackEvent } = useTracking();
    const [isOpen, setIsOpen] = useState(false);
    if (!children) {
      console.error(INVALID_PROP_ERROR);
      return null;
    }
    const onClickHandler = (event) => {
      !dataPrivate ? trackEvent() : void 0;
      if (hasExternalLinkWarning) {
        event == null ? void 0 : event.preventDefault();
        setIsOpen(true);
      } else {
        onClick == null ? void 0 : onClick(event);
      }
    };
    const onKeyDownHandler = (event) => {
      onKeyDown == null ? void 0 : onKeyDown(event);
    };
    const onPrimaryBtnClickHandler = () => {
      trackEvent({
        component: {
          identifier: `${warningPrimaryButtonText}:${href}`,
          type: "ui"
        },
        doNotTrack: false,
        trackType: "navigation"
      });
    };
    const onCloseHandler = () => {
      setIsOpen(false);
      trackEvent({
        component: {
          identifier: `${warningTitle} modal | ${warningSecondaryButtonText}`,
          type: "ui"
        },
        doNotTrack: false,
        trackType: "navigation"
      });
    };
    const customStyles = Array.isArray(sx) ? sx : [sx];
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        CustomStylesLink,
        {
          "data-testid": getDataTestId(COMPONENT_NAME, uniqueId),
          variant,
          underline,
          href: preserveSearchParams && typeof window !== "undefined" ? combineSearchParams(href, window.location.href) : href,
          onClick: onClickHandler,
          onKeyDown: onKeyDownHandler,
          onMouseEnter,
          "aria-label": ariaLabel,
          "aria-labelledby": ariaLabelledBy,
          "aria-describedby": ariaDescribedBy,
          color,
          sx: customStyles,
          ref,
          target,
          ...dataPrivate && { "data-private": "" },
          ...dataBiPrivate && { "data-bi-private": "" },
          ...rest,
          children
        }
      ),
      hasExternalLinkWarning && /* @__PURE__ */ jsx(
        Modal,
        {
          closeAriaLabel: warningCloseAriaLabel,
          hideBackdrop: false,
          modalTitle: warningTitle ? warningTitle : "",
          isOpen,
          onClose: onCloseHandler,
          uniqueId: "external-link-warning-modal",
          buttons: [
            {
              text: warningPrimaryButtonText ? warningPrimaryButtonText : "",
              href,
              action: onPrimaryBtnClickHandler
            },
            {
              text: warningSecondaryButtonText ? warningSecondaryButtonText : "",
              action: onCloseHandler
            }
          ],
          children: /* @__PURE__ */ jsx(Text, { id: "visit-external-link-text", children: warningText })
        }
      )
    ] });
  }
);
Link.displayName = COMPONENT_NAME;
export {
  Link
};
